import { useMemo, useState } from 'react';
import {
  actorLabel,
  formatActivityLine,
  formatActivityWhen,
  groupActivity,
  previewActivityEvents,
  type ActivityDto,
  type ActivityEvent,
  type ActivityFormatOptions,
} from '../../lib/activity-timeline';
import { cn } from '../../lib/utils';
import type { LabelDto, MemberDto } from '../../lib/work-types';
import { Button } from '../ui/button';

type Props = {
  activity: ActivityDto[];
  members: MemberDto[];
  labels: LabelDto[];
  formatOptions?: ActivityFormatOptions;
};

function EventBody({
  event,
  members,
  labels,
  align,
  formatOptions,
}: {
  event: ActivityEvent;
  members: MemberDto[];
  labels: LabelDto[];
  align: 'left' | 'right';
  formatOptions?: ActivityFormatOptions;
}) {
  return (
    <div className={cn(align === 'right' && 'text-right')}>
      <div className={cn('flex flex-wrap items-baseline gap-x-2 text-sm', align === 'right' && 'justify-end')}>
        <span className="font-medium">{actorLabel(event.actor_id, event.actor_kind, members)}</span>
        {event.actor_kind === 'AGENT' ? <span className="text-xs text-muted-foreground">via agent</span> : null}
        <time className="text-xs text-muted-foreground" dateTime={event.created_at}>
          {formatActivityWhen(event.created_at)}
        </time>
      </div>
      <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
        {event.rows.map((entry) => (
          <li key={entry.id}>{formatActivityLine(entry, members, labels, formatOptions)}</li>
        ))}
      </ul>
    </div>
  );
}

export default function ActivityTimeline({ activity, members, labels, formatOptions }: Props) {
  const [expanded, setExpanded] = useState(false);
  const events = useMemo(() => groupActivity(activity), [activity]);
  const { visible, hiddenCount } = previewActivityEvents(events, expanded);

  if (activity.length === 0) {
    return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  }

  return (
    <div className="space-y-2">
      <ol className="relative">
        <span
          className="absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-border"
          aria-hidden
        />
        {visible.map((event, index) => {
          const onRight = index % 2 === 0;
          return (
            <li
              key={event.id}
              className="relative grid grid-cols-[minmax(0,1fr)_1rem_minmax(0,1fr)] items-start gap-x-3 py-3 first:pt-0 last:pb-0"
            >
              {onRight ? <div /> : <EventBody event={event} members={members} labels={labels} align="right" formatOptions={formatOptions} />}
              <span className="relative mx-auto mt-1.5 h-2 w-2 rounded-full bg-foreground" aria-hidden />
              {onRight ? <EventBody event={event} members={members} labels={labels} align="left" formatOptions={formatOptions} /> : <div />}
            </li>
          );
        })}
      </ol>
      {hiddenCount > 0 ? (
        <div className="flex justify-center">
          <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded(true)}>
            Show {hiddenCount} earlier
          </Button>
        </div>
      ) : null}
      {expanded ? (
        <div className="flex justify-center">
          <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded(false)}>
            Show latest
          </Button>
        </div>
      ) : null}
    </div>
  );
}
